import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from './ThemeContext';
import { useEffect, useState } from 'react';
import Navbar from './components/Navbar';
import ProtectedRoute from './components/ProtectedRoute';
import Home from './pages/Home';
import Predict from './pages/Predict';
import About from './pages/About';
import Industries from './pages/Industries';
import ResearchData from './pages/ResearchData';
import ModelBenchmark from './pages/ModelBenchmark';
import TechArchitecture from './pages/TechArchitecture';
import Contact from './pages/Contact';

function CustomCursor() {
  useEffect(() => {
    const dot = document.getElementById('cur-dot');
    const ring = document.getElementById('cur-ring');
    if (!dot || !ring) return;
    let mx = -100, my = -100, rx = -100, ry = -100, raf;
    const move = e => { mx = e.clientX; my = e.clientY; dot.style.left = mx + 'px'; dot.style.top = my + 'px'; };
    const loop = () => { rx += (mx - rx) * 0.13; ry += (my - ry) * 0.13; ring.style.left = rx + 'px'; ring.style.top = ry + 'px'; raf = requestAnimationFrame(loop); };
    const over = e => { if (e.target.closest('a,button,[data-hover]')) ring.classList.add('big'); else ring.classList.remove('big'); };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseover', over);
    raf = requestAnimationFrame(loop);
    return () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseover', over); cancelAnimationFrame(raf); };
  }, []);
  return null;
}

function ViewCounter() {
  useEffect(() => {
    try {
      const k = 'fiveg_views';
      const d = JSON.parse(localStorage.getItem(k) || '{"t":0}');
      d.t = (d.t || 0) + 1; d.last = new Date().toISOString();
      localStorage.setItem(k, JSON.stringify(d));
    } catch (e) {}
  }, []);
  return null;
}

function TimeTravelIntro() {
  const [gone, setGone] = useState(false);
  useEffect(() => { setTimeout(() => setGone(true), 3200); }, []);
  if (gone) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 99990, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 20, animation: 'ttout 0.6s ease 2.6s forwards', pointerEvents: 'none' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@700&display=swap');
        @keyframes ttout{to{opacity:0;visibility:hidden;}}
        @keyframes tunnel{0%{transform:translate(-50%,-50%) scale(0.05);opacity:0;}25%{opacity:1;}100%{transform:translate(-50%,-50%) scale(10);opacity:0;}}
        @keyframes ttwords{0%{opacity:0;letter-spacing:14px;}25%{opacity:1;}85%{opacity:1;}100%{opacity:0;}}
        @keyframes warpline{0%{transform:scaleX(0);opacity:0;}50%{opacity:1;}100%{transform:scaleX(1);opacity:0;}}
      `}</style>
      <div style={{ position: 'absolute', top: '50%', left: '50%', animation: 'tunnel 2.6s ease-in forwards' }}>
        {[50, 100, 160, 230, 310, 400, 500, 610].map((s, i) => (
          <div key={i} style={{ position: 'absolute', width: s, height: s, borderRadius: '50%', border: `1px solid rgba(0,212,255,${0.12 + i * 0.1})`, top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }} />
        ))}
        {Array.from({ length: 16 }, (_, i) => (
          <div key={'l' + i} style={{ position: 'absolute', top: '50%', left: '50%', width: 340, height: 1, background: 'linear-gradient(90deg,transparent,rgba(0,212,255,0.5),transparent)', transformOrigin: 'left center', transform: `rotate(${i * 22.5}deg)`, animation: `warpline 1.4s ease ${i * 0.07}s infinite` }} />
        ))}
      </div>
      <div style={{ fontFamily: "'Orbitron',monospace", fontSize: 14, letterSpacing: 8, color: 'rgba(0,212,255,0.95)', textTransform: 'uppercase', animation: 'ttwords 2.6s ease forwards', position: 'relative', zIndex: 1, textAlign: 'center' }}>
        5G · DELAY · INTELLIGENCE
      </div>
      <div style={{ fontFamily: "'Orbitron',monospace", fontSize: 10, letterSpacing: 4, color: 'rgba(255,255,255,0.35)', position: 'relative', zIndex: 1, animation: 'ttwords 2.6s ease 0.2s forwards', textAlign: 'center' }}>
        Long Short Term Memory · Hybrid LSTM
      </div>
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <style>{`
          *{box-sizing:border-box;margin:0;padding:0;}
          html{scroll-behavior:smooth;}
          body{cursor:none!important;overflow-x:hidden;}
          a,button{cursor:none!important;}
          ::-webkit-scrollbar{width:5px;}
          ::-webkit-scrollbar-track{background:transparent;}
          ::-webkit-scrollbar-thumb{background:rgba(0,150,220,0.3);border-radius:3px;}
          #cur-dot{position:fixed;width:7px;height:7px;border-radius:50%;background:#00d4ff;pointer-events:none;z-index:99999;transform:translate(-50%,-50%);box-shadow:0 0 10px #00d4ff,0 0 24px rgba(0,212,255,0.5);}
          #cur-ring{position:fixed;width:28px;height:28px;border-radius:50%;border:1.5px solid rgba(0,212,255,0.55);pointer-events:none;z-index:99998;transform:translate(-50%,-50%);transition:width 0.18s,height 0.18s,border-color 0.18s,background 0.18s;}
          #cur-ring.big{width:50px;height:50px;border-color:rgba(0,212,255,0.9);background:rgba(0,212,255,0.07);}
          @media(max-width:768px){body{cursor:auto!important;}#cur-dot,#cur-ring{display:none;}a,button{cursor:pointer!important;}}
        `}</style>
        <div id="cur-dot" /><div id="cur-ring" />
        <CustomCursor /><ViewCounter /><TimeTravelIntro />
        <Navbar />
        <Routes>
          {/* ── Public routes ── */}
          <Route path="/" element={<Home />} />
          <Route path="/predict" element={<Predict />} />
          <Route path="/about" element={<About />} />
          <Route path="/industries" element={<Industries />} />
          <Route path="/benchmark" element={<ModelBenchmark />} />
          <Route path="/contact" element={<Contact />} />

          {/* ── Protected routes — passkey required ── */}
          <Route
            path="/research"
            element={
              <ProtectedRoute pageName="Research Data">
                <ResearchData />
              </ProtectedRoute>
            }
          />
          <Route
            path="/architecture"
            element={
              <ProtectedRoute pageName="Tech Architecture">
                <TechArchitecture />
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}
